import * as z from "zod";
import { userFacingError } from "../authorization";
import { mutator } from "./mutator";
import {
  groupMappingsSchema,
  samlMappingSchema,
  syncModeSchema,
} from "./registerSSOProvider";

export const updateSSOProviderSchema = z.object({
  id: z.string(),
  issuer: z.string().trim().min(1).optional(),
  domain: z.string().optional(),
  clientId: z.string().optional(),
  clientSecret: z.string().optional(),
  scopes: z.array(z.string().min(1)).optional(),
  entryPoint: z.string().optional(),
  cert: z.string().optional(),
  metadataUrl: z.string().optional(),
  metadataXml: z.string().optional(),
  mapping: samlMappingSchema.optional(),
  groupMappings: groupMappingsSchema.nullable().optional(),
  jitUserProvisioning: z.boolean().optional(),
  syncMode: syncModeSchema.optional(),
});

export const updateSSOProvider = mutator({
  redactKeys: ["clientSecret", "cert"],
  schema: updateSSOProviderSchema,
  resolve: {
    provider: { kind: "ssoProvider", from: "id" },
  },
  permissions: [
    { permission: "tenant:sso_manage", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({ tx, args }) => {
    const { provider } = args;
    if (args.syncMode === "scim") {
      throw userFacingError(
        "Enabling SCIM requires generating a new token via generateScimToken."
      );
    }
    const updates: Record<string, unknown> = { updatedAt: Date.now() };
    if (args.issuer !== undefined) updates.issuer = args.issuer;
    if (args.domain !== undefined) updates.domain = args.domain;
    if (args.jitUserProvisioning !== undefined) {
      updates.jitUserProvisioning = args.jitUserProvisioning;
    }
    if (args.syncMode !== undefined) {
      updates.syncMode = args.syncMode;
      // The UI sends only syncMode; mirror it into the legacy
      // jitUserProvisioning column so the two stay consistent.
      if (args.jitUserProvisioning === undefined) {
        updates.jitUserProvisioning = args.syncMode !== "off";
      }
      // Leaving SCIM expires the bearer token optimistically; the server mutator
      // also clears the secret hash and rate-limit columns.
      updates.scimTokenExpiresAt = null;
    }

    await tx.mutate.ssoProvider.update({ id: provider.id, ...updates });
  },
});
